import React, { useState, useEffect } from 'react';
import './CookieConsent.css';
import { COOKIE_CONSENT_KEY } from '../../settings/shared/constants';

const COOKIE_CONSENT_EXPIRY_DAYS = 365;

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 500);
    } else {
      // Load saved consent preferences
      try {
        const parsed = JSON.parse(savedConsent);
        setConsent(parsed);
      } catch (e) {
        console.error('Error parsing saved consent:', e);
      }
    }
  }, []);

  const saveConsent = (preferences) => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString(),
      expiry: new Date(Date.now() + COOKIE_CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    setConsent(preferences);
    setShowBanner(false);
    setShowSettings(false);
    
    // Dispatch custom event for other parts of the app to listen to
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: consentData }));
  };

  const handleAcceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    });
  };

  const handleRejectAll = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    });
  };

  const handleSavePreferences = () => {
    saveConsent(consent);
  };

  const handleToggle = (type) => {
    if (type === 'necessary') return; // Cannot disable necessary cookies
    setConsent(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="cookie-consent-overlay" />
      <div className="cookie-consent-banner">
        {!showSettings ? (
          <>
            <div className="cookie-consent-content">
              <div className="cookie-consent-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor"/>
                  <path d="M12.5 7H11.5V9H12.5V7Z" fill="currentColor"/>
                  <path d="M12.5 11H11.5V13H12.5V11Z" fill="currentColor"/>
                  <path d="M12.5 15H11.5V17H12.5V15Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="cookie-consent-text">
                <h4 className="cookie-consent-title">We use cookies</h4>
                <p className="cookie-consent-description">
                  We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                  By clicking "Accept All", you consent to our use of cookies. You can manage your preferences at any time.
                </p>
              </div>
            </div>
            <div className="cookie-consent-actions">
              <button 
                className="btn btn-outline-secondary cookie-btn-secondary" 
                onClick={handleRejectAll}
              >
                Reject All
              </button>
              <button 
                className="btn btn-outline-primary cookie-btn-secondary" 
                onClick={() => setShowSettings(true)}
              >
                Preference
              </button>
              <button 
                className="btn btn-primary cookie-btn-primary" 
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="cookie-consent-content">
              <div className="cookie-consent-text">
                <h4 className="cookie-consent-title">Cookie Preferences</h4>
                <p className="cookie-consent-description">
                Manage your cookie preferences below. You can enable or disable different types of cookies depending on your preferences. Some cookies are essential for the website to function and cannot be disabled.
                </p>
              </div>
            </div>
            <div className="cookie-consent-settings">
              <div className="cookie-setting-item">
                <div className="cookie-setting-info">
                  <h5>Necessary Cookies</h5>
                  <p>These cookies are essential for the website to function properly. They enable core features such as security, network management, and accessibility. Without these cookies, certain services such as secure login or form submissions may not function correctly.
                  These cookies do not store personally identifiable information and cannot be disabled.</p>
                </div>
                <label className="cookie-switch">
                  <input 
                    type="checkbox" 
                    checked={consent.necessary} 
                    disabled 
                    readOnly
                  />
                  <span className="cookie-slider"></span>
                </label>
              </div>
              <div className="cookie-setting-item">
                <div className="cookie-setting-info">
                  <h5>Analytics Cookies</h5>
                  <p>These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. They provide insights such as the number of visitors, pages visited, traffic sources, and other usage statistics that help us improve the website.</p>
                </div>
                <label className="cookie-switch">
                  <input 
                    type="checkbox" 
                    checked={consent.analytics} 
                    onChange={() => handleToggle('analytics')}
                  />
                  <span className="cookie-slider"></span>
                </label>
              </div>
              <div className="cookie-setting-item">
                <div className="cookie-setting-info">
                  <h5>Marketing Cookies</h5>
                  <p>These cookies are used to deliver relevant advertisements to visitors and measure the effectiveness of advertising campaigns. They may track browsing activity across websites to build a profile of user interests.</p>
                </div>
                <label className="cookie-switch">
                  <input 
                    type="checkbox" 
                    checked={consent.marketing} 
                    onChange={() => handleToggle('marketing')}
                  />
                  <span className="cookie-slider"></span>
                </label>
              </div>
              <div className="cookie-setting-item">
                <div className="cookie-setting-info">
                  <h5>Functional Cookies</h5>
                  <p>These cookies enable enhanced functionality and personalization. They may remember your preferences such as language, region, or previously selected settings to provide a more customized experience.</p>
                </div>
                <label className="cookie-switch">
                  <input 
                    type="checkbox" 
                    checked={consent.functional} 
                    onChange={() => handleToggle('functional')}
                  />
                  <span className="cookie-slider"></span>
                </label>
              </div>
            </div>
            <div className="cookie-consent-actions">
              <button 
                className="btn btn-outline-secondary cookie-btn-secondary" 
                onClick={() => setShowSettings(false)}
              >
                Back
              </button>
              <button 
                className="btn btn-primary cookie-btn-primary" 
                onClick={handleSavePreferences}
              >
                Save Preferences
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CookieConsent;

